import { Eye, EyeOff } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import {
  type ResetPasswordFormData,
  resetPasswordSchema,
} from "@/features/auth/schemas/auth.schema"
import { clearTokens } from "@/lib/token-manager"
import { useAuthForm } from "../../hooks/useAuthForm"
import { resetPassword } from "../../services/auth.service"

export function MobileResetPasswordPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get("token") || ""
  const email = searchParams.get("email") || ""

  const [formState, setFormState] = useState<"default" | "success" | "error">("default")
  const [errorMessage, setErrorMessage] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    resetPasswordSchema,
    async (data: ResetPasswordFormData) => {
      await resetPassword(email, token, data.password)
      setFormState("success")
    },
  )

  useEffect(() => {
    if (!token || !email) {
      setFormState("error")
      setErrorMessage(t("resetPassword.invalidToken"))
    }
  }, [token, email, t])

  const passwordRegister = form.register("password")
  const confirmPasswordRegister = form.register("confirmPassword")

  if (formState === "error") {
    return (
      <div className="space-y-6.5">
        <div className="text-center mb-5">
          <h2 className="text-2xl font-semibold leading-normal text-[#F2F0EBE6]">
            {t("resetPassword.errorTitle")}
          </h2>
        </div>

        <div className="space-y-4 text-center">
          <p className="text-primary text-sm">
            {errorMessage || t("resetPassword.errorDescription")}
          </p>
        </div>

        <div className="">
          <Button
            type="button"
            variant="primary"
            size="md"
            className="rounded-full uppercase text-sm font-semibold py-3.75 h-12.5"
            fullWidth
            onClick={() => navigate("/forgot-password")}
          >
            {t("resetPassword.requestNewLink")}
          </Button>
        </div>
      </div>
    )
  }

  if (formState === "success") {
    return (
      <div className="space-y-6.5">
        <div className="text-center mb-5">
          <h2 className="text-2xl font-semibold leading-normal text-[#F2F0EBE6]">
            {t("resetPassword.successTitle")}
          </h2>
        </div>

        <div className="text-center">
          <p className="text-primary text-sm leading-5.5">
            {t("resetPassword.successDescription")}
          </p>
          <p className="text-primary text-sm leading-5.5">
            {t("resetPassword.successDescription2")}
          </p>
        </div>

        <div className="">
          <Button
            type="button"
            variant="primary"
            size="md"
            className="rounded-full uppercase text-sm font-semibold py-3.75 h-12.5"
            fullWidth
            onClick={() => {
              clearTokens()
              navigate("/login", { replace: true })
            }}
          >
            {t("resetPassword.loginButton")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-7.5">
        <div className="text-center mb-7.5">
          <h2 className="text-2xl font-semibold leading-normal text-[#F2F0EBE6]">
            {t("resetPassword.title")}
          </h2>
          <p className="text-primary text-sm leading-6.25 mt-5">{t("resetPassword.description")}</p>
        </div>

        <div className="space-y-6.25">
          <div className="relative">
            <CustomInput
              label={t("resetPassword.passwordLabel")}
              type={showPassword ? "text" : "password"}
              required={false}
              placeholder={t("resetPassword.passwordPlaceholder")}
              {...passwordRegister}
              error={(form.formState.errors.password?.message || serverError) as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                passwordRegister.onChange?.(e)
                clearServerError()
              }}
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          <div className="relative">
            <CustomInput
              label={t("resetPassword.confirmPasswordLabel")}
              type={showConfirmPassword ? "text" : "password"}
              required={false}
              placeholder={t("resetPassword.confirmPasswordPlaceholder")}
              {...confirmPasswordRegister}
              error={form.formState.errors.confirmPassword?.message as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                confirmPasswordRegister.onChange?.(e)
                clearServerError()
              }}
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          <div className="mt-7.5">
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="rounded-full uppercase text-sm font-semibold py-3.75 h-12.5"
              isLoading={isSubmitting}
              fullWidth
            >
              {t("resetPassword.submitButton")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
